"use client";
import React from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Link from "next/link";
import PageTransition from "@/components/animations/PageTransition";
import FadeInView from "@/components/animations/FadeInView";
import StaggerContainer from "@/components/animations/StaggerContainer";
import Breadcrumb from "@/components/Breadcrumb";

const challenges = [
  { icon: "📄", title: "Document Security", desc: "Sensitive client documents and confidential information require robust security controls and access management." },
  { icon: "⚖️", title: "Compliance Requirements", desc: "Professional services firms face regulatory requirements around data handling, privacy, and information security." },
  { icon: "🏠", title: "Remote Work", desc: "Secure, reliable remote access for staff working from home, client sites, or on the road." },
  { icon: "📧", title: "Microsoft 365 Productivity", desc: "Maximise your investment in Microsoft 365 — Teams, SharePoint, email, and collaboration tools fully configured." },
  { icon: "🔒", title: "Client Confidentiality", desc: "Protecting client data is fundamental. We implement the controls that keep your client information secure." },
];

const services = [
  { title: "Managed IT Services", href: "/managed-it-services", desc: "Proactive IT management for professional services firms." },
  { title: "Cybersecurity", href: "/cyber-security", desc: "Layered security protecting client data and business systems." },
  { title: "Cloud & Microsoft 365", href: "/cloud-solutions/microsoft-365", desc: "M365 setup, management, and optimisation for your firm." },
  { title: "Network Solutions", href: "/network-solutions", desc: "Secure, reliable network infrastructure for your office." },
  { title: "Business Communications", href: "/business-communications", desc: "Professional phone systems and unified communications." },
  { title: "ICT Procurement", href: "/ict-procurement", desc: "Source and deploy the right technology for your team." },
];

export default function ProfessionalServicesPage() {
  return (
    <PageTransition>
      <main style={{ background: "#070B14", overflowX: "hidden" }}>
        <Header />
        <Breadcrumb
          items={[
            { label: "Home", href: "/" },
            { label: "Industries", href: "/industries" },
            { label: "Professional Services" },
          ]}
        />
        <section className="relative min-h-[320px] md:min-h-[480px] flex items-center justify-center py-32 overflow-hidden">
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #070B14 0%, #0B1220 40%, #0f1f3d 70%, #070B14 100%)" }} />
          <div className="absolute top-1/3 right-1/4 w-96 h-96 rounded-full blob-1" style={{ background: "radial-gradient(circle, rgba(37,99,235,0.15) 0%, transparent 70%)" }} />
          <div className="relative max-w-container mx-auto px-4 sm:px-6 text-center">
            <FadeInView delay={0}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-pill mb-8" style={{ background: "rgba(37,99,235,0.12)", border: "1px solid rgba(59,130,246,0.25)" }}>
                <span className="text-xl mr-1">💼</span>
                <span className="text-[11px] font-body font-600 uppercase tracking-widest gradient-text">Professional Services</span>
              </div>
            </FadeInView>
            <FadeInView delay={0.15}>
              <h1 className="font-heading font-800 text-display-md md:text-display mb-6" style={{ color: "#F8FAFC", letterSpacing: "-0.03em", lineHeight: "1.05" }}>
                Secure, Reliable IT for<br />
                <span className="gradient-text">Professional Services Firms</span>
              </h1>
            </FadeInView>
            <FadeInView delay={0.3}>
              <p className="text-body-lg leading-[1.75] max-w-3xl mx-auto mb-10" style={{ color: "#94A3B8" }}>
                Pro IT Solutions delivers the security, compliance, and productivity tools that professional services firms need to protect client data and operate efficiently.
              </p>
            </FadeInView>
            <FadeInView delay={0.45}>
              <Link href="/book" className="px-6 sm:px-8 py-3.5 sm:py-4 text-[15px] font-heading font-600 rounded-pill btn-primary">Book a Professional Services IT Review</Link>
            </FadeInView>
          </div>
        </section>

        {/* Challenges */}
        <section className="py-20 lg:py-28" style={{ background: "#0B1220" }}>
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView className="text-center mb-16">
              <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Professional Services IT Challenges</h2>
            </FadeInView>
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch" staggerDelay={0.08}>
              {challenges?.map((c) => (
                <div key={c?.title} className="glass-card rounded-card p-6 h-full flex flex-col">
                  <div className="text-3xl mb-4">{c?.icon}</div>
                  <h3 className="font-heading font-700 text-[24px] mb-3" style={{ color: "#F8FAFC" }}>{c?.title}</h3>
                  <p className="text-body-sm leading-[1.75]" style={{ color: "#94A3B8" }}>{c?.desc}</p>
                </div>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* Services */}
        <section className="py-20">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView className="text-center mb-16">
              <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Services for Professional Services Firms</h2>
            </FadeInView>
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch" staggerDelay={0.08}>
              {services?.map((svc) => (
                <Link key={svc?.title} href={svc?.href} className="group rounded-card-lg p-6 transition-all duration-300 hover:scale-[1.02] h-full flex flex-col" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <h3 className="font-heading font-700 text-[24px] mb-2" style={{ color: "#F8FAFC" }}>{svc?.title}</h3>
                  <p className="text-[14px] font-body mb-4 leading-[1.75]" style={{ color: "#94A3B8" }}>{svc?.desc}</p>
                  <span className="text-[14px] font-body font-medium mt-auto" style={{ color: "#60A5FA" }}>Learn more →</span>
                </Link>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView scale={0.95}>
              <div className="rounded-card-lg p-6 sm:p-10 lg:p-16 text-center" style={{ background: "rgba(37,99,235,0.08)", border: "1px solid rgba(59,130,246,0.2)" }}>
                <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Ready to secure your firm&apos;s IT?</h2>
                <p className="text-body-lg mb-8 leading-[1.75]" style={{ color: "#94A3B8" }}>Let&apos;s discuss your requirements and build a secure, compliant IT environment for your firm.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link href="/book" className="px-6 sm:px-8 py-3.5 sm:py-4 rounded-pill font-heading font-600 btn-primary">Book a Free Review</Link>
                  <Link href="/services" className="px-6 sm:px-8 py-3.5 sm:py-4 rounded-pill font-heading font-500 btn-secondary">View All Services</Link>
                </div>
              </div>
            </FadeInView>
          </div>
        </section>

        <Footer />
      </main>
    </PageTransition>
  );
}
